/**
 * Copyright (c) 2024-present, Bruno Carvalho de Araujo.
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */
import { Query } from '@/gql/graphql'
import { gql, useQuery } from '@apollo/client'
import SectionMessage from '@atlaskit/section-message'
import Select, { type OptionsType } from '@atlaskit/select'
import { Formik, type FormikHelpers, type FormikProps } from 'formik'
import { isEqual } from 'lodash'
import moment from 'moment'
import { useMemo, type FC } from 'react'
import { Button, Col, Form, InputGroup, Row, Spinner } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'
import OfferPreview from './OfferPreview'

const GET_OFFER_CATEGORIES_QUERY = gql`
  query {
    products(where: { hasImage: true }, limit: 999999) {
      sku
      name
      wikimeeMetaVisibleOnSite
    }
    offerCategories(status: [active]) {
      id
      name
      customTheme {
        backgroundColor
        textColor
      }
    }
  }
`

const validationSchema = yup.object().shape({
  id: yup.string(),
  description: yup.string().nullable().default(''),
  sku: yup.string().nullable(),
  store: yup.number().nullable(),
  category: yup.string().nullable(),
  price: yup.number().nullable(),
  oldPrice: yup.number().nullable(),
  measureUnit: yup.string().oneOf(['UN', 'KG']),
  quantity: yup.number().nullable(),
  quantity2: yup.number().nullable(),
  percent: yup.number().nullable()
    .min(0, 'Desconto deve ser maior ou igual a 0')
    .max(100, 'Desconto deve ser menor ou igual a 100')
    .default(0),
  publishedIn: yup.date().nullable()
    .min(moment().utc(false).subtract(1, 'year').toDate(), 'Data de ativação deve ser maior que 1 mês')
    .max(moment().utc(false).add(5, 'years').toDate(), 'Data de ativação deve ser menor que 1 ano')
    .default(moment().utc(false).toDate()),
  unpublishedIn: yup.date().nullable()
    .min(yup.ref('publishedIn'), 'Data de inativação deve ser igual ou maior que a data de ativação')
    .max(moment().utc(false).add(5, 'years').toDate(), 'Data de inativação deve ser menor que 1 ano')
    .default(moment().utc(false).add(1, 'month').toDate()),
  status: yup.string()
    .oneOf(['active', 'inactive', 'deleted'])
    .default('inactive'),
})

export type ValidationSchema = yup.InferType<typeof validationSchema>

export type FormOfferHandleSubmit<T = void> = (
  values: ValidationSchema,
  helpers: FormikHelpers<ValidationSchema>
) => T | Promise<T>

export type Props = {
  onSubmit?: FormOfferHandleSubmit,
  initialValues?: Partial<ValidationSchema>
}

const FormOffer: FC<FormikProps<ValidationSchema>> = ({
  handleSubmit,
  handleChange,
  handleBlur,
  setFieldValue,
  isSubmitting,
  isValid,
  values,
  errors,
  initialValues = {},
}) => {
  const navigate = useNavigate()
  const { data, loading, called } = useQuery<Query>(GET_OFFER_CATEGORIES_QUERY, {
    fetchPolicy: 'network-only'
  })

  const isChanged: boolean = useMemo(() => {
    return !isEqual(values, initialValues)
  }, [values, initialValues])

  const loadProducts = async (inputValue: string): Promise<OptionsType> => {
    return data?.products
      /* ?.filter(product =>
        product.sku.toLowerCase().includes(inputValue.toLowerCase()) ||
        product.name.toLowerCase().includes(inputValue.toLowerCase())
      ) */
      ?.map(product => ({
        label: `${product.sku} - ${product.name} - ${product.wikimeeMetaVisibleOnSite ? 'Visível' : 'Não visível'}`,
        value: product.sku
      })) ?? []
  }

  if (!called || loading) {
    return (
      <Spinner animation="border" />
    )
  }

  return (
    <Row>
      {data?.product?.wikimeeMetaVisibleOnSite === false && values.id !== undefined && (
        <Col md={12} style={{ marginBottom: 20 }}>
          <SectionMessage title="Porque está oferta de produto não está visível no site?" appearance="error">
            <p>
              Esta oferta está vinculada a um produto cuja propriedade <strong>Visível no Site</strong> está configurada como <strong>Não</strong>
              {' '}
              na plataforma <strong>Wikimee</strong>. Por isso, ela não será exibida no site.
              {' '}
              Para corrigir, altere a visibilidade do produto para <strong>Sim</strong> na plataforma <strong>Wikimee</strong>.
              {' '}
              Em seguida, acesse os detalhes do produto e clique em <strong>Sincronizar com o Wikimee</strong>.
            </p>
          </SectionMessage>
        </Col>
      )}
      <Col md={9} className='mb-3'>
        <form onSubmit={handleSubmit}>
          <Row>
            <Form.Group as={Col} md={4} className="mb-3">
              <Form.Label>Código</Form.Label>
              <Form.Control
                type='text'
                name='id'
                value={values.id ?? ''}
                placeholder='O id será gerado automaticamente'
                onChange={handleChange}
                onBlur={handleBlur}
                disabled
              />
              {errors.id && <p>{errors.id}</p>}
            </Form.Group>
            <Form.Group as={Col} md={8} className="mb-3">
              <Form.Label>Descrição da oferta (opcional, utilizado como referência)</Form.Label>
              <Form.Control
                type='text'
                placeholder='Ex: Oferta de lançamento'
                name='description'
                value={values.description ?? ''}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isSubmitting}
              />
              {errors.description && <p>{errors.description}</p>}
            </Form.Group>
            <Form.Group as={Col} md={6} className='mb-3'>
              <Form.Label>Categoria da oferta</Form.Label>
              <Form.Select
                value={values.category ?? ''}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isSubmitting}
                name='category'
              >
                <option>Selecione</option>
                {data?.offerCategories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </Form.Select>
              {errors.category && <p>{errors.category}</p>}
            </Form.Group>
            <Form.Group as={Col} md={6} className='mb-3'>
              <Form.Label>Código SKU do produto</Form.Label>
              <Select
                isDisabled={isSubmitting}
                isLoading={isSubmitting}
                cacheOptions
                value={
                  data?.products
                    ?.filter(product => product.sku === values.sku)
                    .map(product => ({
                      label: `${product.sku} - ${product.name}`,
                      value: product.sku
                    }))
                    ?.shift()
                }
                defaultOptions
                closeMenuOnSelect
                loadOptions={loadProducts}
                loadingMessage={() => 'Carregando produtos...'}
                noOptionsMessage={() => 'Nenhum produto encontrado'}
                placeholder='Selecione o produto'
                onChange={(option) => {
                  setFieldValue('sku', (option as any)?.value ?? '')
                }}
              />
              {errors.sku && <p>{errors.sku}</p>}
            </Form.Group>
            <Form.Group as={Col} md={6} className="mb-3">
              <Form.Label>Preço anterior (de)</Form.Label>
              <InputGroup className="mb-2">
                <Form.Control
                  type='number'
                  placeholder='EX: R$ 3,00'
                  name='oldPrice'
                  min={0}
                  max={99999999}
                  value={values.oldPrice ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isSubmitting}
                />
                <InputGroup.Text>centavos</InputGroup.Text>
              </InputGroup>
              {errors.oldPrice && <p>{errors.oldPrice}</p>}
            </Form.Group>
            <Form.Group as={Col} md={6} className="mb-3">
              <Form.Label>Preço (por)</Form.Label>
              <InputGroup className="mb-2">
                <Form.Control
                  type='number'
                  placeholder='EX: R$ 2,00'
                  name='price'
                  min={0}
                  max={99999999}
                  value={values.price ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isSubmitting}
                />
                <InputGroup.Text>centavos</InputGroup.Text>
              </InputGroup>
              {errors.price && <p>{errors.price}</p>}
            </Form.Group>
            <Form.Group as={Col} md={4} className='mb-3'>
              <Form.Label>Unidade de medida</Form.Label>
              <Form.Select
                value={values.measureUnit ?? ''}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isSubmitting}
                name='measureUnit'
              >
                <option>Selecione</option>
                <option value='UN'>🛒 Unidade (cada)</option>
                <option value='KG'>📦 Quilograma (KG)</option>
              </Form.Select>
              {errors.measureUnit && <p>{errors.measureUnit}</p>}
            </Form.Group>
            <Form.Group as={Col} md={3} className="mb-3">
              <Form.Label>Quantidade</Form.Label>
              <Form.Control
                type='number'
                placeholder='EX: 10'
                name='quantity'
                min={0}
                max={99999999}
                value={values.quantity ?? ''}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isSubmitting}
              />
              {errors.quantity && <p>{errors.quantity}</p>}
            </Form.Group>
            <Form.Group as={Col} md={3} className="mb-3">
              <Form.Label>Quantidade 2</Form.Label>
              <Form.Control
                type='number'
                placeholder='Ex: 2'
                name='quantity2'
                min={0}
                max={99999999}
                value={values.quantity2 ?? ''}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isSubmitting}
              />
              {errors.quantity2 && <p>{errors.quantity2}</p>}
            </Form.Group>
            <Form.Group as={Col} md={2} className="mb-3">
              <Form.Label>Desconto</Form.Label>
              <InputGroup className="mb-2">
                <Form.Control
                  type='number'
                  placeholder='EX: 10'
                  min={0}
                  max={100}
                  name='percent'
                  value={values.percent ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isSubmitting}
                />
                <InputGroup.Text>%</InputGroup.Text>
              </InputGroup>
              {errors.percent && <p>{errors.percent}</p>}
            </Form.Group>
            <Form.Group as={Col} md={4} className='mb-3'>
              <Form.Label>Visibilidade</Form.Label>
              <Form.Select
                value={values.status ?? ''}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isSubmitting}
                name='status'
              >
                <option>Selecione</option>
                <option value='active'>🌎 Pública</option>
                <option value='inactive'>🔒 Privada</option>
              </Form.Select>
              {errors.status && <p>{errors.status}</p>}
            </Form.Group>
            <Form.Group as={Col} md={4} className="mb-3">
              <Form.Label>Data de ativação</Form.Label>
              <Form.Control
                type='datetime-local'
                name='publishedIn'
                value={moment(values.publishedIn).utc(false).format('YYYY-MM-DDTHH:mm:ss') ?? ''}
                onChange={event => {
                  const date = moment(event.target.value).utc(false).toDate()
                  setFieldValue('publishedIn', date)
                }}
                onBlur={handleBlur}
                disabled={isSubmitting}
              />
              {errors.publishedIn && <p>{errors.publishedIn}</p>}
            </Form.Group>
            <Form.Group as={Col} md={4} className="mb-3">
              <Form.Label>Data de inativação</Form.Label>
              <Form.Control
                type='datetime-local'
                name='unpublishedIn'
                value={moment(values.unpublishedIn).utc(false).format('YYYY-MM-DDTHH:mm:ss') ?? ''}
                onChange={event => {
                  const date = moment(event.target.value).utc(false).toDate()
                  setFieldValue('unpublishedIn', date)
                }}
                onBlur={handleBlur}
                disabled={isSubmitting}
              />
              {errors.unpublishedIn && <p>{errors.unpublishedIn}</p>}
            </Form.Group>
            <Col md={12}>
              <Button
                variant="link"
                style={{ marginRight: 10 }}
                onClick={() => navigate('/offers')}
                disabled={isSubmitting}
              >
                Voltar
              </Button>
              <Button
                type="submit"
                disabled={!isValid || isSubmitting || !isChanged}
              >
                Salvar
              </Button>
            </Col>
          </Row>
        </form>
      </Col>
      <Col md={3}>
        <OfferPreview />
      </Col>
    </Row>
  )
}

const FormOfferWrapper: FC<Props> = ({ onSubmit = () => { }, initialValues = {} }) => (
  <Formik
    component={FormOffer}
    validateOnChange
    validateOnBlur
    validateOnMount
    enableReinitialize
    validationSchema={validationSchema}
    initialValues={validationSchema.cast(initialValues)}
    onSubmit={onSubmit}
  />
)

export default FormOfferWrapper
